import {
  Modal,
  Box,
  Typography,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import SelectMarchand from "./SelectMarchand";
import TransfertDetail from "./TransfertDetail";
import BasicButton from "../BasicButton";
import Keypad from "../keypad";
import { Merchant, PartnerTypes } from "../../common/types";
import { getPartnerDetails } from "../../common/storage";
import { createTransfert } from "../../common/makeAPICalls";
import { toast } from "sonner";
import successIcon from "../../assets/successIcon.svg";
import { useTranslation } from "react-i18next";

interface NewTransfertModalProps {
  open: boolean;
  onClose: () => void;
  selectedMerchant?: Merchant | null;
  initialStep?: number;
}

interface TransfertData {
  selectedMerchant: Merchant | null;
  amount: number;
  currency: string;
  paymentReference: string;
  secretCode: string;
  country: string;
  merchantId: string;
}

const NewTransfertModal: React.FC<NewTransfertModalProps> = ({
  open,
  onClose,
  selectedMerchant,
  initialStep = 0,
}) => {
  const { t } = useTranslation();
  const steps = [t("transfertModal.step1"), t("transfertModal.step2"), t("transfertModal.step3")];
  const [activeStep, setActiveStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState<PartnerTypes>();
  const [transfertData, setTransfertData] = useState<TransfertData>({
    selectedMerchant: selectedMerchant ?? null,
    amount: 0,
    currency: "",
    paymentReference: "",
    secretCode: "",
    country: "",
    merchantId: selectedMerchant?.id ?? "",
  });
  const [ tranfertDone, setTransfertDone ] = useState(false);

  let title = "";
  switch (activeStep) {
    case 0:
    case 1:
    case 2:
      title = t("transfertModal.title");
      break;
    case 3:
      title = t("transfertModal.secretCode");
      break;
    default:
      title = "";
  }

  useEffect(() => {
    setPartnerDetails(getPartnerDetails() ?? undefined);
    if(open) {
      setActiveStep(initialStep);
      setTransfertData((prevData) => ({
        ...prevData,
        selectedMerchant: selectedMerchant ?? null,
        merchantId: selectedMerchant?.id ?? "",
      }))
    }
  }, [open, initialStep]);

  useEffect(() => {
    if (partnerDetails) {
      setTransfertData((prevData) => ({
        ...prevData,
        country: partnerDetails?.country,
        currency: partnerDetails?.currency,
      }));
    }
  }, [partnerDetails]);

  useEffect(() => {
    const triggerTransfert = async () => {
      if (
        activeStep === 3 &&
        transfertData.secretCode.toString().length === 4 &&
        transfertData.amount > 0 &&
        transfertData.merchantId
      ) {
        setActiveStep(2);
        setLoading(true);
        try {
          const customerPhone = transfertData.selectedMerchant?.phone;
          const response = (await createTransfert({
            amount: transfertData.amount,
            currency: transfertData.currency,
            country: transfertData.country,
            merchantId: transfertData.merchantId,
            paymentReason: transfertData.paymentReference,
            merchant: {
              secretCode: transfertData.secretCode,
            },
            ...(customerPhone
              ? { customerObject: { phone: customerPhone } }
              : {}),
          })) as { status?: string; details?: string } | null;
          setLoading(false);
          if (response?.status === "succeeded") {
            setTransfertDone(true);
            toast.success(t("transfertModal.transferSuccess"));
            setActiveStep(4);
          } else {
            toast.error(t("transfertModal.errorMessage") + `: ${response?.details ?? ""}`);
            updateTransfertData({
              secretCode: "",
            });
            setActiveStep(2);
            setLoading(false);
          }
        } catch (error) {
          const details =
            error && typeof error === "object" && "details" in error
              ? String((error as { details?: unknown }).details ?? "")
              : String(error);
          const errorMessage = t("transfertModal.errorMessage") + `: ${details}`;
          setLoading(false);
          toast.error(errorMessage);
        }
      }
    };

    triggerTransfert();
  }, [transfertData.secretCode]);

  const handleNext = async () => {
    if (activeStep === 0 && !transfertData.selectedMerchant) {
      toast.error(t("transfertModal.merchantError"));
      return;
    }
    if (activeStep === 1 && !transfertData.amount) {
      toast.error(t("transfertModal.amountError"));
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleClose = () => {
  if (activeStep === 4 || activeStep === 3 || activeStep === 0) {
    onClose();
    setActiveStep(initialStep);
    setLoading(false);

    setTransfertData({
      selectedMerchant: selectedMerchant ?? null,
      amount: 0,
      country: partnerDetails?.country ?? "",
      paymentReference: "",
      secretCode: "",
      currency: partnerDetails?.currency ?? "",
      merchantId: selectedMerchant?.id ?? "",
    });
  } else {
    setActiveStep((prevStep) => prevStep - 1);
  }
};


  const updateTransfertData = (newData: Partial<TransfertData>) => {
    setTransfertData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  };

  const isStepCompleted = (stepIndex: number): boolean => {
    if (stepIndex < 2) {
      // Les deux premières étapes sont complétées normalement
      return activeStep > stepIndex;
    } else if (stepIndex === 2) {
      // La dernière étape (Confirmation) n'est complétée que si tranfertDone est true
      return tranfertDone;
    }
    return false;
  };


  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "60%",
          backgroundColor: "white",
          borderRadius: 2.5,
          boxShadow: 24,
          p: 3.5,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          outline: "none",
          "&:focus": {
            outline: "none",
          },
        }}
      >
        <Typography
          id="modal-modal-title"
          variant="h6"
          component={"h2"}
          sx={{ mt: 0 }}
        >
          {title}
        </Typography>

          <Stepper
            activeStep={activeStep}
            sx={{
              justifyContent: "space-between",
              "& .MuiStepLabel-root": {
                flexDirection: "column",
              },
              "& .MuiStepLabel-labelContainer": {
                marginTop: 1,
              },
              // Active step styling
              "& .MuiStepIcon-root.Mui-active": {
                color: "#E73A6E !important", // Active icon color
              },
              // Completed step styling (optional)
              "& .MuiStepIcon-root.Mui-completed": {
                color: "#20A144 !important", // Completed icon color
              },
            }}
            connector={null}
          >
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {
                completed: isStepCompleted(index)
              };
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

        <Box sx={{ mt: 2 }}>
          {activeStep === 0 && (
            <SelectMarchand
              selectedMerchant={transfertData.selectedMerchant}
              onMerchantSelect={(merchant) =>
                updateTransfertData({
                  selectedMerchant: merchant,
                  merchantId: merchant?.id,
                })
              }
            />
          )}
          {activeStep === 1 && (
            <TransfertDetail
              onDataChange={updateTransfertData}
              transfertData={transfertData}
            />
          )}
          {activeStep === 2 && (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f8f8f8",
                borderRadius: "8px",
              }}
            >
              {/* Grille des informations */}
              <div
                style={{
                  backgroundColor: "#fff",
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                {/* Raison sociale */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                      marginBottom: "4px",
                    }}
                  >
                    {t("transfertModal.merchantName")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "black",
                      fontWeight: "700",
                      width: "50%",
                      textAlign: "left",
                    }}
                  >
                    {transfertData?.selectedMerchant?.name || "Design 360"}
                  </div>
                </div>

                {/* Email */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                      marginBottom: "4px",
                    }}
                  >
                    {t("transfertModal.merchantMail")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "black",
                      fontWeight: "700",
                      width: "50%",
                      textAlign: "left",
                    }}
                  >
                    {transfertData?.selectedMerchant?.email ||
                      "me@design360.digital"}
                  </div>
                </div>

                {/* Référence */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                      marginBottom: "4px",
                    }}
                  >
                    {t("transfertModal.reference")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "black",
                      fontWeight: "700",
                      width: "50%",
                      textAlign: "left",
                    }}
                  >
                    {transfertData?.paymentReference || "Non renseigné"}
                  </div>
                </div>

                {/* Montant avec séparateur */}
                <div
                  style={{
                    gridColumn: "1 / -1", // Prend toute la largeur
                    border: "1px solid #ddd",
                    padding: "10px",
                    marginTop: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#333",
                        marginBottom: "4px",
                      }}
                    >
                      {t("transfertModal.amountLabel")}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                      color: "black",
                      fontWeight: "700",
                      }}
                    >
                      {transfertData?.amount + " " + transfertData?.currency || "Non renseigné"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeStep === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t("transfertModal.secretCodeStep")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("transfertModal.secretCodeDesc")}
                </Typography>
              </div>
              <Keypad
                onCodeChange={(code) =>
                  updateTransfertData({ secretCode: code })
                }
              />
            </div>
          )}
          {activeStep === 4 && (
            <div
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "40px",
                padding: "30px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <img src={successIcon} alt="transfert-validate" />
                <p style={{ color: "#20A144" }}>{t("transfertModal.successMessage")}</p>
              </div>
              <BasicButton
                text="Close"
                onClickEvent={() => handleClose()}
                style={{ backgroundColor: "#A5A5A5", margin: 0 }}
              />
            </div>
          )}
        </Box>
        {activeStep < 4 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <BasicButton
              text={t("button.cancel")}
              onClickEvent={() => handleClose()}
              style={{ backgroundColor: "#A5A5A5", margin: 0 }}
            />
            <BasicButton
              loading={loading}
              text={activeStep < 2 ? t("button.next") : t("button.confirm")}
              onClickEvent={() => handleNext()}
              style={{ margin: 0 }}
              disabled={activeStep === 3}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default NewTransfertModal;
